/**
 * useDeleteDocumentDatabase Hook
 *
 * Custom React Query mutation hook for deleting document databases.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { documentDatabaseService } from '../../../api/document-database.service';
import { getErrorMessage } from '../../../lib/utils/errors';

interface UseDeleteDocumentDatabaseOptions {
  onSuccess?: () => void;
  onError?: (error: string) => void;
}

/**
 * Hook for deleting document database
 * @param tenantId - Tenant ID
 * @param options - Success/error callbacks
 */
export const useDeleteDocumentDatabase = (tenantId: string, options?: UseDeleteDocumentDatabaseOptions) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (documentDBId: string) => documentDatabaseService.deleteDocumentDatabase(tenantId, documentDBId),
    onSuccess: () => {
      // Invalidate document database list query
      queryClient.invalidateQueries({ queryKey: ['documentDatabases', tenantId] });
      options?.onSuccess?.();
    },
    onError: (error: unknown) => {
      const errorMessage = getErrorMessage(error);
      options?.onError?.(errorMessage);
    },
  });
};
